"use client";

import { useId, useMemo, useState } from "react";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from "recharts";
import { EmptyState } from "@/components/empty-state";
import { ItemList } from "@/components/dividend-tracker/actual-vs-projected-chart";
import { YearDetailDialog } from "@/components/dividend-tracker/year-detail-dialog";
import { computeNiceYAxisTicks } from "@/lib/price-chart-utils";
import { formatCurrency, formatCompactCurrency } from "@/lib/format";
import {
  buildYearlyHistoryForecastBars,
  GERMAN_ANNUAL_ALLOWANCE_EUR,
  type YearlyHistoryForecastBar,
  type DividendEvent,
  type ProjectedEntryForAggregation,
} from "@/lib/dividend-tracker/aggregation";
import type { ProjectedMonthlyEntry } from "@/lib/dividend-tracker/load-projection";
import { cn } from "@/lib/utils";

const GREEN = "#3F7A5B";

const PROJECTION_RANGES = [
  { label: "1J", years: 1 },
  { label: "3J", years: 3 },
  { label: "5J", years: 5 },
  { label: "10J", years: 10 },
  { label: "20J", years: 20 },
] as const;
type ProjectionRangeLabel = (typeof PROJECTION_RANGES)[number]["label"];

function YearTooltip({
  active,
  payload,
  currency,
  birthYear,
}: {
  active?: boolean;
  payload?: { payload: YearlyHistoryForecastBar }[];
  currency: string;
  /** Gesetzt nur im Alters-Ansicht-Modus (siehe xAxisMode weiter unten) - Tooltip-Kopf
   * zeigt dann "Alter X" statt des Kalenderjahrs. */
  birthYear?: number;
}) {
  if (!active || !payload?.[0]) return null;
  const point = payload[0].payload;
  return (
    <div
      className="max-w-64 rounded-md border border-hairline bg-card p-3 shadow-lg"
      style={{ boxShadow: "0 4px 16px rgba(22,38,92,0.08)" }}
    >
      <p className="mb-2 text-sm font-medium text-ink">
        {birthYear !== undefined ? `Alter ${point.year - birthYear}` : point.year}
      </p>
      {point.actualNet > 0 && (
        <div className="mb-2">
          <p className="mb-1 flex items-center justify-between gap-3 text-[0.65rem] tracking-wide text-muted-foreground uppercase">
            <span>Erhalten</span>
            <span className="font-tabular text-sm font-semibold normal-case text-ink">
              {formatCurrency(point.actualNet, currency)}
            </span>
          </p>
          {/* Pro-Symbol-Aufschlüsselung (Feedback-Runde 13 Punkt 3, 2026-07-22) - nur für
              Vergangenheits-/laufendes Jahr verfügbar (echte dividend_events kennen die
              Position), Mehrjahres-Zukunftsprognose bleibt bewusst bei der reinen Summe
              oben (projectMonthlyDividendIncome() summiert bereits ohne Pro-Position-Spur,
              siehe buildYearlyHistoryForecastBars()-Docstring). */}
          {point.actualItems.length > 0 && <ItemList items={point.actualItems} currency={currency} />}
        </div>
      )}
      {point.forecastNet > 0 && (
        <div>
          <p className="mb-1 flex items-center justify-between gap-3 text-[0.65rem] tracking-wide text-muted-foreground uppercase">
            <span>{point.actualNet > 0 ? "Noch erwartet" : "Prognose"}</span>
            <span className="font-tabular text-sm font-semibold normal-case text-ink">
              {formatCurrency(point.forecastNet, currency)}{" "}
              <span className="text-[0.65rem] font-normal text-muted-foreground">(netto)</span>
            </span>
          </p>
          {point.forecastItems.length > 0 && <ItemList items={point.forecastItems} currency={currency} netLabel />}
        </div>
      )}
    </div>
  );
}

/**
 * EIN Chart mit Historie + Prognose statt der vorherigen zwei getrennten Charts
 * (`YearlyDividendChart` für die reine Ist-Historie + `DividendIncomeProjection` für
 * die reine Zukunfts-Hochrechnung, backlog.md -> Runde 8 Punkt 3+28, Erik: "zu einem
 * Chart verschmelzen"). Ein Balken je Kalenderjahr: Vergangenheit voll grün (erhalten),
 * laufendes Jahr gestapelt (erhalten + noch erwarteter Rest), Zukunft schraffiert
 * (Depot-Prognose) - gleiche Schraffur-Technik wie `ActualVsProjectedChart`
 * (SVG-Pattern statt reiner Opacity-Abstufung, damit der Prognose-Charakter auch für
 * Farbfehlsichtige/beim Ausdrucken unterscheidbar bleibt).
 *
 * **Reinvestitions-Umschalter**: wählt zwischen `forecastBars`/`forecastBarsReinvested`
 * (beide vom Aufrufer aus `projectMonthlyDividendIncome`/`-Reinvested` vorbereitet) -
 * reine Client-Anzeigeentscheidung, kein neuer Server-Request.
 *
 * **"Gemeinschaftskonto"-Umschalter**: beide Bar-Sets werden bereits mit BEIDEN
 * Sparerpauschbetrag-Grenzen vom Aufrufer vorbereitet (Einzelperson/Gemeinschaftskonto),
 * aus demselben Grund - `buildYearlyHistoryForecastBars()`/`computeNetAmountWithAllowance()`
 * sind reine, schnelle Funktionen, ein Neuberechnen bei jedem Klick clientseitig ist
 * unproblematisch, spart aber die Komplexität, alle vier Kombinationen serverseitig
 * vorzuhalten.
 */
export function DividendHistoryForecastChart({
  historicalYears,
  currentYear,
  currentYearActualNet,
  currentYearForecastNet,
  futureForecastPointsGross,
  futureForecastPointsReinvestedGross,
  currency,
  positionsWithoutReliableCagr,
  selectedYear,
  onSelectYear,
  birthYear,
  events,
  projectionEntries,
  farFutureProjectedEntries,
  farFutureProjectedEntriesReinvested,
  historyOnly,
}: {
  historicalYears: { year: number; netTotal: number }[];
  currentYear: number;
  currentYearActualNet: number;
  currentYearForecastNet: number;
  futureForecastPointsGross: { year: number; grossAnnual: number }[];
  futureForecastPointsReinvestedGross: { year: number; grossAnnual: number }[];
  currency: string;
  positionsWithoutReliableCagr: number;
  selectedYear?: number;
  onSelectYear?: (year: number) => void;
  /** Optional (profiles.birth_year, siehe BirthYearInput) - schaltet den
   * Kalenderjahr-/Alter-Umschalter frei. Ohne Geburtsjahr bleibt die X-Achse wie
   * bisher immer Kalenderjahr, kein sichtbarer Unterschied fuer Nutzer ohne Angabe. */
  birthYear?: number;
  /** Rohe Ereignisse/12-Monats-Prognose (Feedback-Runde 13 Punkt 3, 2026-07-22) - für die
   * Pro-Symbol-Tooltip-Aufschlüsselung der Vergangenheits-/laufenden-Jahr-Balken; ferne
   * Prognosejahre deckt farFutureProjectedEntries unten ab (Feedback-Runde 17 Punkt 27). */
  events: DividendEvent[];
  projectionEntries: ProjectedMonthlyEntry[];
  /** Pro-Position-Aufschlüsselung für Prognosejahre JENSEITS des nahen 12-Monats-
   * Fensters (Feedback-Runde 17 Punkt 27) - schließt genau die Lücke aus dem Docstring
   * oben. Nur Jahre > currentYear (siehe app/dividenden-tracker/page.tsx), wird unten mit
   * projectionEntries zusammengeführt statt das laufende Jahr zu vermischen. */
  farFutureProjectedEntries: ProjectedEntryForAggregation[];
  farFutureProjectedEntriesReinvested: ProjectedEntryForAggregation[];
  /** Vom Aufrufer gesteuert (backlog.md Punkt 11) - CurrencySection zeigt diese Komponente
   * jetzt ueber zwei eigene Reiter ("Historie"/"Prognose") statt einer internen Checkbox
   * hier, damit die Prognose als eigenstaendiger, auffindbarer Bereich statt eines
   * Checkbox-Details erscheint. */
  historyOnly: boolean;
}) {
  const patternId = `dividendForecastHatch-${useId()}`;
  const [range, setRange] = useState<ProjectionRangeLabel>("10J");
  const [reinvest, setReinvest] = useState(false);
  const [jointAccount, setJointAccount] = useState(false);
  const [showAge, setShowAge] = useState(false);
  const [detailBar, setDetailBar] = useState<YearlyHistoryForecastBar | null>(null);
  const ageOffset = showAge && birthYear !== undefined ? birthYear : undefined;

  const selectedYears = PROJECTION_RANGES.find((r) => r.label === range)!.years;
  const allowanceLimit = jointAccount ? GERMAN_ANNUAL_ALLOWANCE_EUR * 2 : GERMAN_ANNUAL_ALLOWANCE_EUR;
  const activeFutureForecast = reinvest ? futureForecastPointsReinvestedGross : futureForecastPointsGross;
  const activeFarFutureProjectedEntries = reinvest ? farFutureProjectedEntriesReinvested : farFutureProjectedEntries;
  // Nahes 12-Monats-Fenster (projectionEntries) + ferne Prognosejahre
  // (activeFarFutureProjectedEntries, deckt nur Jahre > currentYear ab, siehe
  // app/dividenden-tracker/page.tsx) zusammengeführt - buildYearlyHistoryForecastBars()
  // bucketet beide ohnehin nur nach demselben Jahr, keine Überschneidung möglich.
  const combinedProjectedEntries = useMemo(
    () => [...projectionEntries, ...activeFarFutureProjectedEntries],
    [projectionEntries, activeFarFutureProjectedEntries],
  );

  const bars = useMemo(
    () =>
      buildYearlyHistoryForecastBars(
        historicalYears,
        currentYear,
        currentYearActualNet,
        currentYearForecastNet,
        activeFutureForecast.filter((p) => p.year <= currentYear + selectedYears),
        allowanceLimit,
        events,
        combinedProjectedEntries,
      ),
    [
      historicalYears,
      currentYear,
      currentYearActualNet,
      currentYearForecastNet,
      activeFutureForecast,
      selectedYears,
      allowanceLimit,
      events,
      combinedProjectedEntries,
    ],
  );

  // "Ohne Prognose" heisst konsequent auch: kein schraffierter Rest mehr im laufenden
  // Jahr (forecastNet auf 0), nicht nur ein Weglassen der angehaengten Zukunftsjahre -
  // sonst wuerde trotz aktiviertem Toggle noch eine Schraffur sichtbar bleiben.
  const displayBars = useMemo(
    () =>
      historyOnly
        ? bars.filter((b) => b.year <= currentYear).map((b) => ({ ...b, forecastNet: 0, forecastItems: [] }))
        : bars,
    [bars, historyOnly, currentYear],
  );

  const yAxisTicks = useMemo(
    () => computeNiceYAxisTicks(displayBars.map((b) => b.actualNet + b.forecastNet), true),
    [displayBars],
  );

  if (bars.every((b) => b.actualNet === 0 && b.forecastNet === 0)) {
    return <EmptyState card={false} message="Noch keine Daten für eine Dividenden-Hochrechnung vorhanden." />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          {/* Ohne sichtbare Prognose-Balken (Historie-Reiter) hat die Reinvest-Annahme
              keine erkennbare Wirkung mehr - ausgeblendet statt wirkungslos bedienbar. */}
          {!historyOnly && (
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={reinvest} onChange={(e) => setReinvest(e.target.checked)} />
              Dividenden reinvestieren
            </label>
          )}
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={jointAccount} onChange={(e) => setJointAccount(e.target.checked)} />
            Gemeinschaftskonto ({formatCompactCurrency(GERMAN_ANNUAL_ALLOWANCE_EUR * 2, currency)} Freibetrag)
          </label>
        </div>
        {birthYear !== undefined && (
          <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
            {(["Kalenderjahr", "Alter"] as const).map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => setShowAge(label === "Alter")}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  (label === "Alter") === showAge ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Zeitraum-Regler steuert nur, wie weit die (hier ausgeblendete) Prognose reicht -
            ergibt bei "Nur Historie" keinen sichtbaren Unterschied, daher ausgeblendet statt
            wirkungslos bedienbar zu bleiben. */}
        {!historyOnly && (
          <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
            {PROJECTION_RANGES.map((r) => (
              <button
                key={r.label}
                type="button"
                onClick={() => setRange(r.label)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  range === r.label ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {/* accessibilityLayer={false} (Feedback-Runde 13 Punkt 4, 2026-07-22): gleicher
              gelber-Fokusring-Fix wie im Fairer-Wert-Simulator (siehe dortiger Kommentar in
              fair-value-simulator.tsx fuer die volle Erklaerung) - entfernt tabIndex/role an
              der Recharts-RootSurface-Quelle, die eigene Klick-Interaktion oben gibt bereits
              klares visuelles Feedback (Cell-Opacity). */}
          <BarChart
            data={displayBars}
            margin={{ top: 12, right: 8, left: 4, bottom: 0 }}
            onClick={(state) => {
              const index = state?.activeTooltipIndex;
              if (typeof index !== "number") return;
              onSelectYear?.(displayBars[index].year);
              setDetailBar(displayBars[index]);
            }}
            accessibilityLayer={false}
            // Recharts' prozentualer Default (10%) bezieht sich auf die Kategorie-
            // Bandbreite - bei wenigen Balken (kurze Zeitspannen) wird dadurch jede Luecke
            // absolut betrachtet unverhaeltnismaessig breit (Feedback-Runde 18 Punkt 8,
            // 2026-07-24). Kleinerer fester Prozentsatz unter 10 Jahren Zeitspanne, Default
            // ab 10J beibehalten (dort ohnehin schon dicht genug).
            barCategoryGap={selectedYears < 10 ? "4%" : "10%"}
          >
            {/* Ruhiger gestaltet, gleiche Anpassung wie actual-vs-projected-chart.tsx
                (backlog.md Punkt 4.1, "wirken unruhig fürs Auge") - Muster bewusst
                beibehalten (Farbfehlsichtigkeit/Druck), nur deutlich dezenter. */}
            <defs>
              <pattern id={patternId} width="10" height="10" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                <rect width="10" height="10" fill={GREEN} fillOpacity={0.22} />
                <line x1="0" y1="0" x2="0" y2="10" stroke={GREEN} strokeWidth="1.25" />
              </pattern>
            </defs>
            <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
            <XAxis
              dataKey="year"
              tickFormatter={(v: number) => (ageOffset !== undefined ? String(v - ageOffset) : String(v))}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => formatCompactCurrency(v, currency)}
              width={64}
              axisLine={false}
              tickLine={false}
            />
            {/* zIndex noetig (backlog.md -> [Punkt 12], Bug: Tooltip lag hinter dem Chart,
                Achsenbeschriftung ueberlappte den Hover-Inhalt) - Rechartsʼ Tooltip-Wrapper
                setzt ohne explizite Angabe keinen eigenen z-index, wodurch er je nach
                umgebendem Stacking-Context hinter spaeter gemalten SVG-Elementen (z.B.
                Achsenbeschriftungen) verschwinden kann. */}
            <Tooltip
              cursor={false}
              wrapperStyle={{ zIndex: 50 }}
              content={<YearTooltip currency={currency} birthYear={ageOffset} />}
            />
            <Legend
              formatter={(value) => (value === "actualNet" ? "Erhalten (netto)" : "Prognose (netto)")}
              wrapperStyle={{ fontSize: 12 }}
              iconType="rect"
            />
            {/* BUGFIX (Feedback-Runde 13 Punkt 1, 2026-07-22): fill hier direkt (nicht nur auf
                den Cells darunter) noetig, damit Rechartsʼ Auto-Legend die richtige Farbe
                zeigt - ohne das liest die Legend keine Farbe vom Bar und faellt auf ihr
                eigenes Default (schwarz) zurueck, obwohl die Balken selbst korrekt gruen
                sind. */}
            {/* Eckradius (Feedback-Runde 19 Punkt 12, 2026-07-27): von 5px/nur-oben auf
                8px (~--radius-sm) angehoben UND erstmals auch unten am actualNet-Segment
                (vorher komplett eckig) - richtet sich am Projekt-Radius-System aus
                (kleinster Standard 8.4px), nicht an einem beliebigen Wert. Bewusst NICHT
                die Gruen-Farbe selbst geaendert: GREEN entspricht bereits exakt dem
                zentralen --positive-Design-Token, eine Farbaenderung waere eine groessere
                Marken-Entscheidung als diese reine Politur-Aufgabe. */}
            <Bar dataKey="actualNet" name="actualNet" stackId="dividends" fill={GREEN} radius={[0, 0, 8, 8]} isAnimationActive={false} cursor="pointer" activeBar={{ fillOpacity: 1 }}>
              {displayBars.map((b) => (
                <Cell key={b.year} fill={GREEN} fillOpacity={selectedYear === undefined || selectedYear === b.year ? 1 : 0.35} />
              ))}
            </Bar>
            <Bar
              dataKey="forecastNet"
              name="forecastNet"
              stackId="dividends"
              fill={`url(#${patternId})`}
              stroke={GREEN}
              strokeOpacity={0.5}
              radius={[8, 8, 0, 0]}
              isAnimationActive={false}
              cursor="pointer"
              activeBar={{ strokeOpacity: 1, strokeWidth: 2 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-muted-foreground">
        {/* Feedback-Runde 13 Punkt 2 (2026-07-22): Formulierung umgestellt - "eigene
            historische Wachstumsrate je Position" steht jetzt vorne als Hauptaussage, die
            15-%-Grenze folgt erst danach explizit als Obergrenze/Ausnahmefall statt am
            Satzende leicht ueberlesbar zu wirken (Erik: liest sich sonst wie eine pauschale
            15-%-Annahme statt eines seltenen Deckels). */}
        {historyOnly ? (
          <>Nur bereits erhaltene Dividenden, keine Prognose.</>
        ) : (
          <>
            Schraffiert = Prognose, hochgerechnet mit der eigenen historischen
            Wachstumsrate jeder Position (nach oben hin bei 15 % p. a. gedeckelt, meist
            deutlich darunter genutzt).{" "}
            {formatCurrency(GERMAN_ANNUAL_ALLOWANCE_EUR, currency)} Sparerpauschbetrag/Jahr
            angenommen ({jointAccount ? "verdoppelt fürs Gemeinschaftskonto" : "Einzelperson"}
            ), keine weiteren Investitionen.
            {reinvest && " Reinvestitions-Annahme: Dividenden kaufen jährlich zusätzliche Anteile zum heutigen Kurs."}
            {positionsWithoutReliableCagr > 0 &&
              ` ${positionsWithoutReliableCagr} Position${positionsWithoutReliableCagr === 1 ? "" : "en"} ohne ausreichende Historie wird${positionsWithoutReliableCagr === 1 ? "" : "en"} ohne Wachstum fortgeschrieben.`}
            {" "}Rein rechnerisch, keine Zusage über die Zukunft.
          </>
        )}
        {onSelectYear && " Klick auf einen Balken zeigt die Details dieses Jahres."}
      </p>

      <YearDetailDialog
        bar={detailBar}
        currency={currency}
        currentYear={currentYear}
        birthYear={ageOffset}
        open={detailBar !== null}
        onOpenChange={(next) => {
          if (!next) setDetailBar(null);
        }}
      />
    </div>
  );
}
